import css from './ProductCard.module.css';
import { ReactComponent as HeartOutline } from '../../../assets/icons/heart/outline.svg';
import { ReactComponent as HeartSolid } from '../../../assets/icons/heart/solid.svg';
import { ReactComponent as VerifiedBadge } from '../../../assets/icons/verified-badge.svg';
import { ReactComponent as NotforSaleTag } from '../../../assets/icons/not-for-sale-tag.svg';
import { ReactComponent as Dots } from '../../../assets/icons/dots.svg';
import { ReactComponent as Edit } from '../../../assets/icons/edit.svg';
import { ReactComponent as Trash } from '../../../assets/icons/trash.svg';
import { useIntl } from 'react-intl';
import { formatMoney } from '../../../util/currency';
import NamedLink from '../../NamedLink/NamedLink';
import { createSlug } from '../../../util/urlHelpers';
import classNames from 'classnames';
import { bool } from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { ensureOwnListing } from '../../../util/data';
import { FormattedMessage } from '../../../util/reactIntl';
import Modal from '../../Modal/Modal';
import Button from '../../Button/Button';
import {
  ProductConditionDisplay,
  ProductGenderDisplay,
  ProductSizeDisplay,
} from '../../../enums/product-card-enums';
import useFavoriteToggle from '../../../hooks/useFavoriteToggle';
import { jerseyTypeOptions } from '../../../util/formFieldOptions';

const onManageDisableScrolling = (componentId, scrollingDisabled = true) => {
  /*console.log('Toggling Modal - scrollingDisabled currently:', componentId, scrollingDisabled);*/
};

const ProductCard = ({
  product,
  isSearchPage = false,
  isCurrentUser,
  isFavoritesPage,
  onCloseListing,
  onDiscardDraftListing,
  isLandingPage
}) => {
  const intl = useIntl();
  const [editActionsOpen, setEditActionsOpen] = useState(false);
  const [hideListing, setHideListing] = useState(false);
  const actionsRef = useRef(null);

  const [isOpen, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setEditActionsOpen(false);
  };
  const firstImage =
    Array.isArray(product?.images) &&
      product.images.length > 0
      ? product.images[0]
      : null;

  const image =
    firstImage?.attributes?.variants?.['scaled-medium'] ||
    firstImage?.attributes?.variants?.['product-card-variant'] ||
    firstImage?.attributes?.variants?.['scaled-small'] ||
    null;

  const image2x =
    firstImage?.attributes?.variants?.['scaled-medium-2x'] ||
    firstImage?.attributes?.variants?.['product-card-variant-2x'] ||
    null;

  const isDraft = product?.attributes?.state === 'draft';
  const isSold =
    product?.currentStock?.attributes?.quantity === 0 || product?.currentStock === null;
  const currentListing = ensureOwnListing(product);
  const authorImage =
    product?.author?.profileImage?.attributes?.variants['profile-avatar'] || undefined;
  const price = product?.attributes?.price;
  const priceText = price ? formatMoney(intl, price) : null;
  const slug = createSlug(product.attributes.title);
  const id = product.id.uuid;

  // Use the favorite toggle hook
  const { onFavoriteClick, isFavorited, isToggling } = useFavoriteToggle(product.id.uuid);

  const handleOnFavoriteClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (isFavoritesPage) {
      // This means that the product is already favorited
      // so any click on the button will unfavorite the product
      // so we are going to hide it.
      // When the user refreshes the page it will be gone then
      // setHideListing(true);
    }

    onFavoriteClick(e);
  };

  useEffect(() => {
    if (!editActionsOpen) return; // only listen while open

    const handlePointerDown = event => {
      // if click/touch is outside the action area, close
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setEditActionsOpen(false);
      }
    };

    // Use mousedown and touchstart for snappy response
    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, { passive: true, capture: true });

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, { capture: true });
    };
  }, [editActionsOpen]);

  const handleDiscardListing = async listingId => {
    try {
      setOpen(false);
      // Sharetribe only allows discarding a listing while it's still a draft.
      // Once it's published, the listing can only be closed - it can't be
      // deleted outright - so use the close API for that case instead.
      if (isDraft) {
        await onDiscardDraftListing(listingId);
      } else {
        await onCloseListing(listingId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const size =
    ProductSizeDisplay[(product.attributes?.publicData?.size)] ||
    product.attributes?.publicData?.size;

  const condition =
    ProductConditionDisplay[(product.attributes?.publicData?.condition)] ||
    product.attributes?.publicData?.condition;

  const gender =
    ProductGenderDisplay[(product.attributes?.publicData?.gender)] ||
    product.attributes?.publicData?.gender;

  // Helper function to get label from jersey type key
  const getJerseyTypeLabel = (key) => {
    const option = jerseyTypeOptions.find(opt => opt.key === key);
    return option ? option.label : key;
  };

  const firstTagKey = product?.attributes?.publicData && product?.attributes?.publicData['jersey_type'] && product?.attributes?.publicData['jersey_type'][0];
  const firstTag = firstTagKey ? getJerseyTypeLabel(firstTagKey) : null;
  const numberOfAdditionalTags = product?.attributes?.publicData && product?.attributes?.publicData['jersey_type'] && product?.attributes?.publicData['jersey_type'].length - 1;

  if (hideListing) {
    return null;
  }

  return (
    <div className={classNames(css.wrapper, {
      [css.landingPageCard]: isLandingPage
    })}>
      <NamedLink
        className={classNames(css.namedLink, { [css.searchPage]: isSearchPage })}
        name={isDraft ? "EditListingPage" : "ListingPage"}
        params={isDraft ? { id, slug, type: 'edit', tab: 'details' } : { id, slug }}
      >
       <div className={css.cardContainer}>
         <div className={css.head}>
          <div className={css.tagsFavorite}>
            {product?.attributes?.publicData && product?.attributes?.publicData['jersey_type'] && product?.attributes?.publicData['jersey_type'].length > 0 && (
              <div className={css.jerseyTypes}>
                <ul>
                  <li>{firstTag}</li>
                  {numberOfAdditionalTags > 0 && <li>+{numberOfAdditionalTags} </li>}
                </ul>
              </div>
            )}

            <button
              type="button"
              className={classNames(css.favoriteButton, { [css.isToggling]: isToggling })}
              onClick={event => handleOnFavoriteClick(event)}
              aria-label={isFavorited ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
              aria-busy={isToggling}
              disabled={isToggling}
            >
              {isFavorited ? <HeartSolid /> : <HeartOutline />}
            </button>
          </div>

          {isDraft && <div className={css.draftIndicator}>DRAFT</div>}
          {/* {isSold && <div className={css.draftIndicator}>SOLD</div>} */}

          {image?.url ? (
            <img
              key={image?.url}
              src={image?.url}
              srcSet={
                image2x?.url
                  ? `${image?.url} 1x, ${image2x?.url} 2x`
                  : undefined
              }
              width={image.width}
              height={image.height}
              alt={product.attributes.title}
              loading="lazy"
              decoding="async"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div
              className={css.imagePlaceholder}
              role="img"
              aria-label={`${product.attributes.title} placeholder image`}
            />
          )}
        </div>
        <div className={css.content}>
          <div className={css.primaryData}>{product.attributes?.title}</div>
          <ul className={css.secondaryData}>
            <li>{size}</li>
            <li className={css.separator}></li>
            <li>{condition}</li>
            <li className={css.separator}></li>
            <li>{gender}</li>
          </ul>
          <div className={css.user}>
            {authorImage ? (
              <img
                src={authorImage.url}
                width={authorImage.width}
                height={authorImage.height}
                alt={`${product.author?.attributes?.profile?.publicData?.username} image`}
                className={css.authorImage}
              />
            ) : (
              <div className={css.authorImagePlaceholder}>
                {product.author?.attributes?.profile?.abbreviatedName}
              </div>
            )}
            <span className={css.username}>
              {product.author?.attributes?.profile?.publicData?.username}
            </span>
            {product.author?.attributes?.profile?.metadata['verified_by_marketplace'] && (
              <VerifiedBadge className={css.verifiedBadge} />
            )}
          </div>
          {product?.attributes?.publicData?.for_sale_option === 'not_for_sale' ||
            priceText === null ? (
            <div className={css.notForSale}>
              <NotforSaleTag />
              <div className={css.price}>-</div>
            </div>
          ) : (
            <div className={css.price}>{priceText}</div>
          )}
        </div>
       </div>
      </NamedLink>

      {isCurrentUser && (
        <>
          <div className={css.editActions} ref={actionsRef}>
            <button
              type="button"
              className={css.editTriggerButton}
              onClick={() => setEditActionsOpen(!editActionsOpen)}
            >
              <Dots />
            </button>
            {editActionsOpen && (
              <div className={css.editActionItems}>
                <NamedLink
                  name="EditListingPage"
                  className={css.editLink}
                  params={{ id, slug, type: 'edit', tab: 'details' }}
                >
                  <Edit /> <FormattedMessage id="UserProfile.ListingCard.Edit" />
                </NamedLink>
                <button type="button" className={css.deleteButton} onClick={handleOpen}>
                  <Trash /> <FormattedMessage id="UserProfile.ListingCard.Close" />
                </button>
              </div>
            )}
          </div>

          <Modal
            isOpen={isOpen}
            usePortal
            scrollLayerClassName={css.confirmationModalScroll}
            containerClassName={css.confirmationModalContainer}
            contentClassName={css.confirmationModalContent}
            onClose={() => {
              setOpen(false);
            }}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <h3 className={css.modalTitle}>
              <FormattedMessage id="UserProfile.ListingCard.ConfirmationModal.Title" />
            </h3>

            <p className={css.modalText}>
              <FormattedMessage id="UserProfile.ListingCard.ConfirmationModal.Text" />
            </p>

            <div className={css.modalActions}>
              <Button
                type="button"
                className={css.modalPrimaryButton}
                onClick={() => handleDiscardListing(currentListing.id.uuid)}
              >
                <FormattedMessage id="UserProfile.ListingCard.ConfirmationModal.Close" /> <Trash />
              </Button>
              <Button
                type="button"
                className={css.modalSecondaryButton}
                onClick={() => setOpen(false)}
              >
                <FormattedMessage id="UserProfile.ListingCard.ConfirmationModal.Cancel" />
              </Button>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

ProductCard.propTypes = {
  isSearchPage: bool,
};

export default ProductCard;
